import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type StoreService from 'scaleflow/services/store';
import type ChatService from 'scaleflow/services/chatv2';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type { Owner } from '@ember/test-helpers/build-owner';

export interface MainSidebarSignature {
  // The arguments accepted by the component
  Args: object;
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}
export default class MainSidebarComponent extends Component<MainSidebarSignature> {
  @service declare store: StoreService;
  @service declare currentUser: any;
  @service declare betaToggle: any;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare organisation: any;
  @service declare chatv2: ChatService;

  @tracked activePanel: string | null = null;

  private _closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(owner: Owner, args: MainSidebarSignature['Args']) {
    super(owner, args);
    void this.store.findAll('department');
    void this.store.findAll('team');
  }

  @action
  openPanel(name: string): void {
    this._cancelClose();
    this.activePanel = name;
  }

  @action
  closePanel(): void {
    this._cancelClose();
    this.activePanel = null;
  }

  @action
  scheduleClose(): void {
    this._cancelClose();
    this._closeTimer = setTimeout(() => {
      this.activePanel = null;
    }, 150);
  }

  @action
  cancelClose(): void {
    this._cancelClose();
  }

  private _cancelClose(): void {
    if (this._closeTimer !== null) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  get activeTeams(): any[] {
    const teams = this.store
      .peekAll('team')
      .filter((item: any) => item.active === true);
    return teams;
  }

  get departments() {
    return this.store.peekAll('department');
  }

  get teamRoute() {
    let defaultRoute = 'operations.teams.team.home';
    const currentRoute = this.router.currentRoute?.name ?? '';
    if (currentRoute.includes('operations.teams.team'))
      defaultRoute = currentRoute;
    if (currentRoute.includes('departments.department'))
      defaultRoute = `operations.teams.team.${currentRoute.split('.').pop()}`;
    return defaultRoute;
  }

  get departmentRoute() {
    let defaultRoute = 'departments.department.home';
    const currentRoute = this.router.currentRoute?.name ?? '';
    if (currentRoute.includes('departments.department'))
      defaultRoute = currentRoute;
    if (
      currentRoute.includes('operations.teams.team') &&
      !currentRoute.includes('check-in')
    )
      defaultRoute = `departments.department.${currentRoute.split('.').pop()}`;
    return defaultRoute;
  }

  @action
  handleChatToggle() {
    void this.chatv2.populateChatWindow();
    this.chatv2.toggleChatWindow(true);
    this.chatv2.toggleRecentChatsIsVisible(true);
  }

  @action
  isRouteActive(key: string): boolean {
    const name = this.router.currentRoute?.name ?? '';
    return name === key || name.startsWith(`${key}.`);
  }

  @action
  openDepartmentCollapse(department: any) {
    const id = department.id;
    if (!id) return;
    const menuCollapseButton = document.querySelector(
      `.menu-collapse-container [data-department="${department.id}"]`,
    );
    if (!menuCollapseButton) return;

    //if collapse is open
    const collapseIsOpen = !!document.querySelector(
      `.menu-collapse-container [data-department="${department.id}"] + .menu-collapse`,
    );
    if (collapseIsOpen) return;
    else
      (
        document.querySelector(
          `.menu-collapse-container [data-department="${department.id}"] button`,
        ) as HTMLElement
      )?.click?.();
  }
}
